import { constants, metadata, dbViews, http, network } from 'lib_ui-services';
import { format } from 'date-fns';
import notHandledHere from '../notHandledHere';
import downloadTheFile from '../exportHelpers/exportHelper';
import { addTimezoneToColumns } from '../exportHelpers/utils';
import { getSelectedIds, getDeselectedIds } from '../exportHelpers/selection';
import getAllFeatureFlags from '../../../../../utilities/getAllFeatureFlags';

const { getDbView } = dbViews;
const { EXPORT_TYPES } = constants;

const _p = {
    addTimezoneToColumns,
    getDbView,
    downloadTheFile,
    EXPORT_TYPES,
    http,
    network,
    getPrettyRelationName: metadata.getPrettyRelationName,
    getRelationMetadata: metadata.getRelationMetadata,
    checkExportConditions,
    postToServer,
    confirmExport
};

export const _private = _p;

export default {
    verb: 'willExport',
    excludedNamespaceRelations: notHandledHere.concat([{ namespace: 'report' }, { namespace: 'item', relation: 'item-patch' }]),
    description: 'Prepare the export metadata',
    priority: 0,
    logic: will_export,
    onError
};

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").Context} Context
 *
 * @typedef ExportDefinition
 * @property {Array} [data] OPTIONAL Array of data to use instead of using it from a view
 * @property {string} [viewName] OPTIONAL name of LokiJS view if not passing in data directly
 * @property {string} [filePrefix] OPTIONAL Title/file prefix
 * @property {Array.<{propertyName:string, label:string, dataType:string}>} columns
 * @property {Record<string, any>} [selection] OPTIONAL the grid's multi-select state, see exportHelpers/selection
 * @property {boolean} [limitSyncSize] OPTIONAL whether the relation only syncs part of its records
 * @property {boolean} [isConnected] OPTIONAL whether the browser is online
 */

/**
 * @param {{
 *   data: ExportDefinition;
 *   prerequisiteResults: object[];
 *   context: Context;
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 */
async function will_export({ data, context, dispatch }) {
    const { namespace, relation, type } = context;
    const featureFlags = getAllFeatureFlags(context);
    const relationLabel = _p.getPrettyRelationName(namespace, relation);

    // Get the navigation selection if needed (for multi-inventory exports)
    const { columns, navigationSelection } = data;

    // if this rule does not know how to export this type, skip it. (if it is blank, we'll use a default)
    if (type && !EXPORT_TYPES[type]) return;

    const filePrefix = data.filePrefix || relationLabel || `export_${relation}`;
    const timestamp = format(new Date(), "yyyyMMMdd'T'hhmmaaa");
    const fileName = `${filePrefix}_${timestamp}`;

    let exportFields = {
        title: fileName,
        fileName,
        navigationSelection,
        featureFlags,
        namespace,
        relation,
        relationLabel,
        fileType: EXPORT_TYPES[type] || EXPORT_TYPES.csv,
        filePrefix,
        columns
    };

    // simple sanity check to make sure there are "some" records to export at all
    // if we'd use the filter for cached-online data, but the user had not fetched more yet
    // it would otherwise give a possibly result.
    // making sure there is at least "something", would allow us to continue even if the stuff we really want is all only on the server.
    // note, the actual filtering of the data happens in the doingExport rule.
    const countResponse = await dispatch(
        {
            criteria: { 'meta.deleted': { $exists: false } }
        },
        { verb: 'count', namespace, relation },
        true
    );
    let count = countResponse?.result ? countResponse.result[0] : 0;

    if (!count) {
        return sendNotification(`No ${relationLabel} to Export`, dispatch);
    }

    const canPerformServerSideExport = await _p.checkExportConditions(data, context, dispatch);
    if (canPerformServerSideExport) {
        // Add the timezone to the columns so that the server knows the timezone to use for the export
        const columnsWithTimezone = _p.addTimezoneToColumns(exportFields.columns);
        exportFields.columns = columnsWithTimezone;
        try {
            const response = await _p.postToServer({ data, exportFields, dispatch });
            if (response && response._id) {
                await sendNotification(`Preparing to export ${relationLabel} records`, dispatch);
                // add the reportId to the exportFields so doingExport will know how to handle the export data
                exportFields.reportId = response._id;
            } else {
                return { error: { field: {}, aborted: true }, data: {} };
            }
        } catch (error) {
            // A session without server-side export route access (e.g. missing the feature flag
            // that gates report:generate in metadata) gets a 403 here. Rather than hard-failing,
            // fall through without a reportId so doingExport uses the client-side export path.
            if (error?.status !== 403) {
                throw error;
            }
            await sendNotification(
                `You do not have permission to export ${relationLabel} on the server. Exporting locally available records instead.`,
                dispatch
            );
        }
    }

    return { data: { ...data, ...exportFields, count }, context };
}

/**
 * Posts the export request to the server.
 * This is a synchronous call to the server to create the report metadata record.
 *
 * @param {{
 *   data: ExportDefinition;
 *   exportFields: object;
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 * }} parameters
 */
async function postToServer({ data, exportFields, dispatch }) {
    // Get user confirmation before exporting
    const confirmedExport = await _p.confirmExport({ relationLabel: exportFields.relationLabel, dispatch });
    if (!confirmedExport) return;

    // get the filters from the view
    const view = await _p.getDbView(exportFields.namespace, exportFields.relation, data.viewName);
    const filters = view.viewCriteria.getFiltersFromView();

    exportFields.reportType = 'adhoc_export';

    // pass in the filters as query parameters
    const url = _p.http.getQueryUrl('report', 'generate', getReportFilters(filters, data.selection), undefined, {
        includeEndpointSuffix: false
    });
    const response = await _p.http.post(url, exportFields, true);
    return response;
}

/**
 * Turn the grid's view filters into the filters for a report/generate request, so the server
 * builds its export over the same records the user is looking at.
 * @param {Record<string, any>} viewFilters getFiltersFromView() output for the grid being exported
 * @param {Record<string, any>} [selection] the grid's multi-select state
 * @returns {Record<string, any>} filters to hand to getQueryUrl
 */
function getReportFilters(viewFilters, selection) {
    const filters = { ...viewFilters };
    // Both of these throw 'not implemented' from getUriComponent, so they can never reach the
    // server anyway, and leaving them in would only fail the request.
    delete filters.caseSensitive;
    delete filters.alsoSearchWithAscii;

    const deselectedIds = getDeselectedIds(selection);
    if (deselectedIds.length > 0) {
        // The user chose "select all" and then unticked a few rows, so leave those few out.
        // searchCriteria is the filter that becomes the `criteria` query parameter, and { $oid }
        // tells the server to read the string as a mongo ObjectId.
        filters.searchCriteria = {
            ...filters.searchCriteria,
            _id: { $nin: deselectedIds.map(_id => ({ $oid: _id })) }
        };
    }
    return filters;
}

/**
 * Checks if the server-side export can proceed based on certain conditions.
 * @param {ExportDefinition} data - The export data definition.
 * @param {Context} context - The context of the operation.
 * @param {function} dispatch - The dispatch function to send messages.
 * @returns {Promise<boolean>} - True if server-side export can proceed, otherwise false.
 */
async function checkExportConditions(data, context, dispatch) {
    if (!data.limitSyncSize) {
        return false;
    }

    // Rows the user ticked are exported from the local view instead. doingExport is the only
    // step that narrows records down to the ticked ones, and a server side export skips it.
    // Those records are always in the view - the user had to see a row to tick it.
    if (getSelectedIds(data.selection).length > 0) {
        return false;
    }

    const DISALLOWED_EXPORT_TYPES = ['pdf', 'html'];
    if (DISALLOWED_EXPORT_TYPES.includes(context.type)) {
        await sendNotification(
            `Server side report generation of ${context.type} files is not supported. Using default client side report generation.`,
            dispatch
        );
        return false;
    }

    if (!data.isConnected) {
        await sendNotification(
            'You must be online to perform a server side export. Using default client side report creation.',
            dispatch
        );
        return false;
    }

    return true;
}

/**
 * Sends a notification message to the user.
 * @param {string} message - The message to be sent.
 * @param {function} dispatch - The dispatch function to send messages.
 */
async function sendNotification(message, dispatch) {
    await dispatch(
        {
            addToList: false,
            message: message,
            variant: 'warning',
            type: constants.notificationTypes.TOAST
        },
        { verb: 'pop', namespace: 'application', relation: 'notification' }
    );
}

function onError({ error, dispatch }) {
    dispatch(
        {
            message: `Export failed: ${error.message}`,
            variant: 'error',
            type: constants.notificationTypes.TOAST
        },
        { verb: 'pop', namespace: 'application', relation: 'notification' }
    );
    throw error;
}

async function confirmExport({ relationLabel, dispatch }) {
    return new Promise(resolve => {
        const message = [
            `This action will export your ${relationLabel} records, and could take some time to complete.`,
            'Refreshing or closing this page will interrupt the export, but you can continue to navigate within the application.',
            'Continue?'
        ];
        dispatch(
            {
                message,
                okButtonText: 'YES',
                icon: 'warning',
                iconColorStyle: constants.COLOR_STYLES.Warning,
                variant: constants.VARIANT.Warning,
                okAction: () => resolve(true),
                cancelAction: () => resolve(false)
            },
            { verb: 'confirm', namespace: 'application', relation: 'user' }
        );
    });
}
